import React, { useState, useMemo, useEffect, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import axios from 'axios';
import { ForecastContext, WorkspaceContext } from '../../../objects/Context';
import './Variance.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

const CHART_WEEKS = 8;

// ─── Colors (matching CSS variables) ────────────────────────────────────────
const COLORS = {
  forecast:      '#488FF3',
  forecastFaded: 'rgba(72, 143, 243, 0.30)',
  actual:        '#36454F',
  actualFaded:   'rgba(54, 69, 79, 0.28)',
  placeholder:   'rgba(200, 205, 212, 0.32)',
  placeholderBorder: 'rgba(200, 205, 212, 0.45)',
};

const getRowLabel = (rowData, rowId) => {
  if (rowData?.label) return rowData.label;
  return rowId.replace(/[-_]/g, ' ');
};

const SECTIONS = [
  { id: 'revenue',     label: 'Revenue' },
  { id: 'op_inflows',  label: 'Operating Inflows' },
  { id: 'op_outflows', label: 'Operating Outflows' },
  { id: 'financing',   label: 'Financing' },
];

// ─── Helpers ────────────────────────────────────────────────────────────────
const fmtCurrency = (v) => {
  if (v == null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    currencySign: 'accounting',
  }).format(v);
};

const fmtCompact = (v) => {
  if (v == null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
    notation: 'compact',
  }).format(v);
};

const fmtWeekLabel = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00Z');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

const fmtPercent = (v) => {
  if (v == null || !isFinite(v)) return '—';
  if (v < 0) return `(${(Math.abs(v) * 100).toFixed(1)}%)`;
  if (v > 0) return `+${(v * 100).toFixed(1)}%`;
  return '0.0%';
};

// ─── Component ──────────────────────────────────────────────────────────────
const SKELETON_ROWS = 3;

const Variance = () => {
  const navigate = useNavigate();
  const { currentForecast } = useContext(ForecastContext);
  const { workspace } = useContext(WorkspaceContext);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWeekIdx, setSelectedWeekIdx] = useState(0);
  const [collapsedSections, setCollapsedSections] = useState(
    () => new Set(SECTIONS.map((s) => s.id))
  );

  // Drill-down state
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [drilldownData, setDrilldownData] = useState(new Map());
  const drilldownDataRef = useRef(drilldownData);
  useEffect(() => { drilldownDataRef.current = drilldownData; }, [drilldownData]);
  const expandedRowsRef = useRef(expandedRows);
  useEffect(() => { expandedRowsRef.current = expandedRows; }, [expandedRows]);

  // Reset drill-down when switching weeks
  useEffect(() => {
    setExpandedRows(new Set());
    setDrilldownData(new Map());
  }, [selectedWeekIdx]);

  const fetchSubaccounts = useCallback(async (rowId, rowData, snapshotDate) => {
    const actualsSource = rowData?.actuals_source;
    if (!actualsSource?.core_table || !workspace?.id) return;

    setDrilldownData(prev => new Map(prev).set(rowId, { loading: true, accounts: null, error: null }));
    try {
      const snapshotDateObj = new Date(snapshotDate + 'T00:00:00Z');
      const nextWeek = new Date(snapshotDateObj);
      nextWeek.setUTCDate(nextWeek.getUTCDate() + 7);
      // Use a 13-week lookback so accounts with $0 activity in this specific week
      // still appear if they were active in any recent period.
      const miniCalendar = {
        granularity: 'week',
        forecast_start: nextWeek.toISOString().split('T')[0],
        actual_period_count: 13,
        forecast_period_count: 0,
      };

      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/components/subaccounts`,
        {
          workspaceId: workspace.id,
          forecastId: currentForecast?.id || null,
          actualsSource,
          calendar: miniCalendar,
        },
        { withCredentials: true }
      );
      setDrilldownData(prev => new Map(prev).set(rowId, { loading: false, accounts: data.accounts || [], error: null }));
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load accounts';
      setDrilldownData(prev => new Map(prev).set(rowId, { loading: false, accounts: null, error: msg }));
    }
  }, [workspace?.id, currentForecast?.id]);

  const toggleRowDrilldown = useCallback((rowId, rowData, snapshotDate) => {
    if (!rowData?.actuals_source?.core_table) return;
    const isOpen = expandedRowsRef.current.has(rowId);
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (isOpen) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
    if (!isOpen && !drilldownDataRef.current.has(rowId)) {
      fetchSubaccounts(rowId, rowData, snapshotDate);
    }
  }, [fetchSubaccounts]);

  const handleAccountClick = useCallback((acct) => {
    const params = new URLSearchParams();
    if (acct.name) params.set('sourceAccounts', acct.name);
    navigate(`/forecast/transactions?${params.toString()}`);
  }, [navigate]);

  const toggleSection = (sectionId) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  useEffect(() => {
    const forecastId = currentForecast?.id;
    if (!forecastId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    axios
      .get(
        `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/snapshots/${forecastId}/hydrated?limit=10`,
        { withCredentials: true }
      )
      .then((res) => {
        if (cancelled) return;
        const sorted = (res.data || []).reverse();
        setSnapshots(sorted);
        setSelectedWeekIdx(Math.max(0, sorted.length - 1));
      })
      .catch((err) => {
        console.error('Failed to load snapshots:', err);
        if (!cancelled) setSnapshots([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [currentForecast?.id]);

  const sectionConfig = useMemo(() => {
    if (snapshots.length === 0) return [];
    const snap = snapshots[0];
    return SECTIONS.map((section) => ({
      ...section,
      rowKeys: Object.keys(snap[section.id]?.rows || {}),
    }));
  }, [snapshots]);

  const selectedSnapshot = snapshots[selectedWeekIdx];

  const realCount = snapshots.length;

  // ── Chart data (always 8 weeks, padding with placeholders) ──
  const chartData = useMemo(() => {
    if (realCount === 0) return null;

    const realLabels = snapshots.map((s) => fmtWeekLabel(s.snapshot_date));
    const realActuals = snapshots.map((s) => s.cash?.change?.actual ?? 0);
    const realForecasts = snapshots.map((s) => s.cash?.change?.forecast ?? 0);

    const padCount = Math.max(0, CHART_WEEKS - realCount);

    const lastDate = new Date(snapshots[realCount - 1].snapshot_date + 'T00:00:00Z');
    const placeholderLabels = Array.from({ length: padCount }, (_, i) => {
      const d = new Date(lastDate);
      d.setUTCDate(d.getUTCDate() + 7 * (i + 1));
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
    });

    const avgActual = realActuals.reduce((sum, v) => sum + v, 0) / realCount;
    const placeholderActual = avgActual;
    const placeholderForecast = avgActual * 0.85;

    const labels = [...realLabels, ...placeholderLabels];
    const actualData = [...realActuals, ...Array(padCount).fill(placeholderActual)];
    const forecastData = [...realForecasts, ...Array(padCount).fill(placeholderForecast)];

    const actualBg = labels.map((_, i) => {
      if (i >= realCount) return COLORS.placeholder;
      return i === selectedWeekIdx ? COLORS.actual : COLORS.actualFaded;
    });
    const forecastBg = labels.map((_, i) => {
      if (i >= realCount) return COLORS.placeholder;
      return i === selectedWeekIdx ? COLORS.forecast : COLORS.forecastFaded;
    });
    const actualBorder = labels.map((_, i) =>
      i >= realCount ? COLORS.placeholderBorder : COLORS.actual
    );
    const forecastBorder = labels.map((_, i) =>
      i >= realCount ? COLORS.placeholderBorder : COLORS.forecast
    );

    return {
      labels,
      datasets: [
        {
          label: 'Actual',
          data: actualData,
          backgroundColor: actualBg,
          borderColor: actualBorder,
          borderWidth: 1,
          borderRadius: 6,
          borderSkipped: false,
        },
        {
          label: 'Forecast',
          data: forecastData,
          backgroundColor: forecastBg,
          borderColor: forecastBorder,
          borderWidth: 1,
          borderRadius: 6,
          borderSkipped: false,
        },
      ],
    };
  }, [snapshots, selectedWeekIdx, realCount]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    layout: { padding: { top: 28 } },
    onClick: (_, elements) => {
      if (elements.length > 0 && elements[0].index < realCount) {
        setSelectedWeekIdx(elements[0].index);
      }
    },
    plugins: {
      legend: { display: false },
      title: { display: false },
      datalabels: {
        anchor: 'end',
        align: 'end',
        offset: 2,
        font: { family: 'Almarai-Regular', size: 10.5, weight: '600' },
        color: (ctx) => {
          if (ctx.dataIndex >= realCount) return 'transparent';
          return ctx.datasetIndex === 0 ? COLORS.actual : COLORS.forecast;
        },
        formatter: (value, ctx) => ctx.dataIndex >= realCount ? '' : fmtCompact(value),
      },
      tooltip: {
        enabled: true,
        filter: (tooltipItem) => tooltipItem.dataIndex < realCount,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 12,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: (ctx) => `${ctx.dataset.label || ''}: ${fmtCurrency(ctx.parsed.y)}`,
        },
      },
    },
    scales: {
      x: {
        display: true,
        grid: { display: false },
        ticks: {
          font: { family: 'Almarai-Regular', size: 11 },
          color: (ctx) => ctx.index >= realCount ? '#c8cdd4' : '#73818f',
        },
      },
      y: {
        display: true,
        grid: { color: 'rgba(0,0,0,0.06)', drawBorder: false },
        ticks: {
          font: { family: 'Almarai-Regular', size: 11 },
          color: '#73818f',
          callback: (v) => fmtCompact(v),
        },
      },
    },
  }), [realCount]);

  // ── Loading state ──
  if (loading) {
    return (
      <div className="wrapper variance-wrapper">
        <div className="variance-page">
          <div className="variance-header">
            <div className="variance-title-group">
              <h1 className="variance-title">Variance Analysis</h1>
              <h2 className="variance-subtitle">Actuals vs Forecast</h2>
            </div>
          </div>
          <div className="variance-empty">
            <p className="variance-empty-detail">Loading snapshots…</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Empty state ──
  if (!selectedSnapshot) {
    return (
      <div className="wrapper variance-wrapper">
        <div className="variance-page">
          <div className="variance-header">
            <div className="variance-title-group">
              <h1 className="variance-title">Variance Analysis</h1>
              <h2 className="variance-subtitle">Actuals vs Forecast</h2>
            </div>
          </div>
          <div className="variance-empty">
            <h3 className="variance-empty-heading">No data available yet</h3>
            <p className="variance-empty-detail">
              Variance snapshots are created after each rollforward. <br />
              Check back in once the first rollforward is complete!
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Detail metrics ──
  const cashChange = selectedSnapshot.cash?.change || {};
  const varianceDollar = (cashChange.actual ?? 0) - (cashChange.forecast ?? 0);
  const variancePct = cashChange.forecast ? varianceDollar / Math.abs(cashChange.forecast) : null;

  return (
    <div className="wrapper variance-wrapper">
      <div className="variance-page">
        {/* ── Header ── */}
        <div className="variance-header">
          <div className="variance-title-group">
            <h1 className="variance-title">Variance Analysis</h1>
            <h2 className="variance-subtitle">Actuals vs Forecast</h2>
          </div>
          <div className="variance-header-legend">
            <span className="legend-item">
              <span className="legend-dot" style={{ backgroundColor: COLORS.actual }} />
              Actual
            </span>
            <span className="legend-item">
              <span className="legend-dot" style={{ backgroundColor: COLORS.forecast }} />
              Forecast
            </span>
          </div>
        </div>

        {/* ── Top Section: Chart ── */}
        <div className="variance-chart-section">
          <div className="variance-chart-container">
            {chartData && <Bar data={chartData} options={chartOptions} />}
          </div>
        </div>

        {/* ── Bottom Section: Detail ── */}
        <div className="variance-detail-section">
          {/* Week selector + summary pills */}
          <div className="variance-detail-header">
            <div className="week-selector">
              {snapshots.map((snap, idx) => (
                <button
                  key={snap.snapshot_date}
                  className={`week-btn ${idx === selectedWeekIdx ? 'active' : ''}`}
                  onClick={() => setSelectedWeekIdx(idx)}
                >
                  {fmtWeekLabel(snap.snapshot_date)}
                </button>
              ))}
            </div>
            <div className="variance-summary-pills">
              <div className="pill actual-pill">
                <span className="pill-label">Actual Δ</span>
                <span className="pill-value">{fmtCurrency(cashChange.actual)}</span>
              </div>
              <div className="pill forecast-pill">
                <span className="pill-label">Forecast Δ</span>
                <span className="pill-value">{fmtCurrency(cashChange.forecast)}</span>
              </div>
              <div className={`pill ${varianceDollar >= 0 ? 'positive' : 'negative'}`}>
                <span className="pill-label">Variance</span>
                <span className="pill-value">
                  {fmtCurrency(varianceDollar)} ({fmtPercent(variancePct)})
                </span>
              </div>
            </div>
          </div>

          {/* Cash summary row */}
          <div className="variance-cash-row">
            <div className="cash-cell">
              <span className="cash-label">Beginning Cash</span>
              <div className="cash-values">
                <span className="cash-actual">{fmtCurrency(selectedSnapshot.cash?.begin_cash?.actual)}</span>
                <span className="cash-forecast">{fmtCurrency(selectedSnapshot.cash?.begin_cash?.forecast)}</span>
              </div>
            </div>
            <div className="cash-cell">
              <span className="cash-label">Cash Change</span>
              <div className="cash-values">
                <span className="cash-actual">{fmtCurrency(cashChange.actual)}</span>
                <span className="cash-forecast">{fmtCurrency(cashChange.forecast)}</span>
              </div>
            </div>
            <div className="cash-cell">
              <span className="cash-label">Ending Cash</span>
              <div className="cash-values">
                <span className="cash-actual">{fmtCurrency(selectedSnapshot.cash?.end_cash?.actual)}</span>
                <span className="cash-forecast">{fmtCurrency(selectedSnapshot.cash?.end_cash?.forecast)}</span>
              </div>
            </div>
          </div>

          {/* Detail table */}
          <div className="variance-table-wrapper">
            <table className="variance-table">
              <thead>
                <tr>
                  <th className="col-label">Line Item</th>
                  <th className="col-number">Actual</th>
                  <th className="col-number">Forecast</th>
                  <th className="col-number">Variance ($)</th>
                  <th className="col-number">Variance (%)</th>
                </tr>
              </thead>
              <tbody>
                {sectionConfig.map((section) => {
                  const sectionBlock = selectedSnapshot[section.id];
                  const sectionTotal = sectionBlock?.total;
                  const sectionVariance = sectionTotal ? sectionTotal.actual - sectionTotal.forecast : 0;
                  const sectionVariancePct = sectionTotal && sectionTotal.forecast !== 0
                    ? sectionVariance / Math.abs(sectionTotal.forecast)
                    : null;

                  const isCollapsed = collapsedSections.has(section.id);

                  return (
                    <React.Fragment key={section.id}>
                      <tr
                        className="section-header-row section-collapsible"
                        onClick={() => toggleSection(section.id)}
                      >
                        <td className="col-label section-label">
                          <span className={`section-chevron ${isCollapsed ? '' : 'open'}`}>&#9656;</span>
                          {section.label}
                        </td>
                        <td className="col-number section-value">{fmtCurrency(sectionTotal?.actual)}</td>
                        <td className="col-number section-value">{fmtCurrency(sectionTotal?.forecast)}</td>
                        <td className={`col-number section-value ${sectionVariance >= 0 ? 'positive' : 'negative'}`}>
                          {fmtCurrency(sectionVariance)}
                        </td>
                        <td className={`col-number section-value ${sectionVariance >= 0 ? 'positive' : 'negative'}`}>
                          {fmtPercent(sectionVariancePct)}
                        </td>
                      </tr>
                      {!isCollapsed && [...section.rowKeys]
                        .filter((rowId) => sectionBlock?.rows?.[rowId])
                        .sort((a, b) => {
                          const aRow = sectionBlock.rows[a];
                          const bRow = sectionBlock.rows[b];
                          return Math.abs(bRow.actual - bRow.forecast) - Math.abs(aRow.actual - aRow.forecast);
                        })
                        .flatMap((rowId) => {
                        const rowData = sectionBlock.rows[rowId];
                        const rowVar = rowData.actual - rowData.forecast;
                        const rowVarPct = rowData.forecast !== 0 ? rowVar / Math.abs(rowData.forecast) : null;
                        const hasDrilldown = !!rowData.actuals_source?.core_table;
                        const isDrilldownOpen = expandedRows.has(rowId);
                        const dd = drilldownData.get(rowId);

                        const elements = [
                          <tr
                            key={rowId}
                            className={`detail-row ${hasDrilldown ? 'drilldown-eligible' : ''} ${isDrilldownOpen ? 'drilldown-open' : ''}`}
                            onClick={hasDrilldown ? () => toggleRowDrilldown(rowId, rowData, selectedSnapshot.snapshot_date) : undefined}
                          >
                            <td className="col-label row-label-cell">
                              {hasDrilldown && (
                                <span className={`var-drilldown-arrow ${isDrilldownOpen ? 'open' : ''}`}>
                                  {isDrilldownOpen ? '▾' : '▸'}
                                </span>
                              )}
                              {getRowLabel(rowData, rowId)}
                            </td>
                            <td className="col-number">{fmtCurrency(rowData.actual)}</td>
                            <td className="col-number">{fmtCurrency(rowData.forecast)}</td>
                            <td className={`col-number ${rowVar >= 0 ? 'positive' : 'negative'}`}>
                              {fmtCurrency(rowVar)}
                            </td>
                            <td className={`col-number ${rowVar >= 0 ? 'positive' : 'negative'}`}>
                              {fmtPercent(rowVarPct)}
                            </td>
                          </tr>
                        ];

                        if (isDrilldownOpen) {
                          if (dd?.loading) {
                            elements.push(
                              ...Array.from({ length: SKELETON_ROWS }, (_, i) => (
                                <tr key={`${rowId}-skel-${i}`} className="detail-row var-drilldown-row var-skeleton-row">
                                  <td className="col-label var-acct-label">
                                    <div className="var-skeleton-bar" style={{ width: `${55 + i * 15}%` }} />
                                  </td>
                                  <td className="col-number"><div className="var-skeleton-bar var-skeleton-val" /></td>
                                  <td className="col-number" />
                                  <td className="col-number" />
                                  <td className="col-number" />
                                </tr>
                              ))
                            );
                          } else if (dd?.error) {
                            elements.push(
                              <tr key={`${rowId}-err`} className="detail-row var-drilldown-row">
                                <td colSpan={5} className="var-drilldown-status var-drilldown-error">{dd.error}</td>
                              </tr>
                            );
                          } else if (dd?.accounts?.length > 0) {
                            elements.push(
                              ...dd.accounts.map((acct) => {
                                const acctVal = acct.values?.[selectedSnapshot.snapshot_date] ?? null;
                                return (
                                  <tr
                                    key={`${rowId}-acct-${acct.id}`}
                                    className="detail-row var-drilldown-row var-drilldown-clickable"
                                    onClick={() => handleAccountClick(acct)}
                                    title={`View transactions for ${acct.name}`}
                                  >
                                    <td className="col-label var-acct-label">
                                      <div className="var-acct-label-inner">
                                        <span className="var-acct-name">{acct.name}</span>
                                        {acct.subtype && <span className="var-acct-subtype">{acct.subtype}</span>}
                                        <span className="var-acct-arrow">→</span>
                                      </div>
                                    </td>
                                    <td className="col-number">{fmtCurrency(acctVal ?? 0)}</td>
                                    <td className="col-number" />
                                    <td className="col-number" />
                                    <td className="col-number" />
                                  </tr>
                                );
                              })
                            );
                          } else if (dd?.accounts?.length === 0) {
                            elements.push(
                              <tr key={`${rowId}-empty`} className="detail-row var-drilldown-row">
                                <td colSpan={5} className="var-drilldown-status">No account detail available.</td>
                              </tr>
                            );
                          }
                        }

                        return elements;
                      })}
                    </React.Fragment>
                  );
                })}

                <tr className="section-header-row cash-summary-row">
                  <td className="col-label section-label">Net Cash Change</td>
                  <td className="col-number section-value">{fmtCurrency(cashChange.actual)}</td>
                  <td className="col-number section-value">{fmtCurrency(cashChange.forecast)}</td>
                  <td className={`col-number section-value ${varianceDollar >= 0 ? 'positive' : 'negative'}`}>
                    {fmtCurrency(varianceDollar)}
                  </td>
                  <td className={`col-number section-value ${varianceDollar >= 0 ? 'positive' : 'negative'}`}>
                    {fmtPercent(variancePct)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Variance;
